import { useMemo } from 'react';
import moment, { type Moment } from 'moment-timezone';
import { useSelector } from 'react-redux';
import { intervalSelector, realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';
import { MAX_INTERVAL, validateInterval } from '../helpers';

function startOfMinute(date: Date | number | Moment) {
  return moment(date).startOf('minute').valueOf();
}

function getInterval(
  interval: { from: Date | number | Moment; to: Date | number | Moment },
  live: boolean,
) {
  if (live) {
    return interval;
  }
  return {
    from: startOfMinute(interval.from),
    to: startOfMinute(interval.to),
  };
}

const dimensions = [
  'network_request_response_code',
  'network_request_url_host',
  'network_request_url_path',
];

const ONE_DAY = 86400;

function getMessage(
  stacked: boolean,
  validDimension: boolean,
  enableMetricFilter: boolean,
  validInterval: boolean,
) {
  if (!validDimension) {
    return 'Dimension is not available';
  }
  if (!stacked && enableMetricFilter) {
    return 'Widget is not available for Sampled Secondary Filters';
  }
  if (!validInterval) {
    const max =
      MAX_INTERVAL > ONE_DAY
        ? `${Math.round(MAX_INTERVAL / ONE_DAY)} days`
        : `${Math.round(MAX_INTERVAL / 3600)} hours`;
    return `Widget is not available for more than ${max}, Please select a date and time accordingly.`;
  }
  return '';
}

export function useAvailable(enableSSF?: boolean, enableTimeSlice?: boolean) {
  const interval = useSelector(intervalSelector);
  const allDimensionList = useSelector(allDimensionsListSelector);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const chartInterval = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);

  const alignedInterval = useMemo(() => {
    return getInterval(enableTimeSlice ? chartInterval : interval, !!realTimeOnTrends);
  }, [chartInterval, enableTimeSlice, interval, realTimeOnTrends]);

  const validInterval = validateInterval(alignedInterval);
  const validDimension = dimensions.every((dimension) =>
    allDimensionList.find((d) => d.field === dimension),
  );
  // return enableSSF ? metricFilter.length > 0 : validInterval;
  const enableMetricFilter = !!enableSSF && !!metricFilter.length;
  const stacked = validDimension && (enableMetricFilter ? false : validInterval); // not available when metricFilter is not empty
  const treemap = validDimension && (enableMetricFilter || validInterval);
  return {
    interval: alignedInterval,
    stacked,
    treemap,
    enableMetricFilter,
    message: {
      stacked: getMessage(stacked, validDimension, enableMetricFilter, validInterval),
      treemap: getMessage(true, validDimension, enableMetricFilter, validInterval),
    },
  };
}
